/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  Autocomplete,
  Button,
  Stack,
  TextField,
  Chip,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  FormCancelButton,
  FormDialog,
  FormDialogActions,
  FormDialogContent,
  FormDialogTitle,
  getSubmitButtonProps,
} from '@marageti/z4-lib';
import { AddedFailureReason } from '@marageti/z4-sdk/lib/travel';
import {
  getFailureReasonLabel,
  getSelectableFailureReasons,
  needsBudgetAmount,
} from './helpers';
import { FailureReasonOption, ScreeningCriteriaMap } from './types';

type AddFailureReasonDialogProps = {
  screeningCriteriaMap?: ScreeningCriteriaMap;
  excludedCodes: string[];
  passedReasons?: string[];
  isSaving: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (drafts: AddedFailureReason[]) => void;
};

const AddFailureReasonDialog = ({
  screeningCriteriaMap,
  excludedCodes,
  passedReasons = [],
  isSaving,
  open,
  onClose,
  onSubmit,
}: AddFailureReasonDialogProps) => {
  const [drafts, setDrafts] = useState<AddedFailureReason[]>([]);
  const [selectedOption, setSelectedOption] = useState<FailureReasonOption | null>(null);

  const selectableOptions = getSelectableFailureReasons(
    screeningCriteriaMap,
    [...excludedCodes, ...drafts.map((draft) => draft.reason)],
  );

  const hasMissingAmount = drafts.some((draft) => needsBudgetAmount(draft.reason, draft.comment, passedReasons));

  const addSelectedReason = () => {
    if (!selectedOption) {
      return;
    }

    setDrafts((current) => {
      if (current.some((draft) => draft.reason === selectedOption.code)) {
        return current;
      }

      return [...current, { reason: selectedOption.code }];
    });
    setSelectedOption(null);
  };

  const handleClose = () => {
    if (isSaving) {
      return;
    }

    setDrafts([]);
    setSelectedOption(null);
    onClose();
  };

  return (
    <FormDialog large open={open} ariaLabelledby="add-failure-reason-title">
      <FormDialogTitle handleClose={handleClose} id="add-failure-reason-title">
        Add failure reason
      </FormDialogTitle>
      <FormDialogContent>
        <Stack useFlexGap spacing={2} sx={{ pb: 4 }}>
          <Stack alignItems="flex-end" direction="row" spacing={1}>
            <Autocomplete
              disabled={isSaving}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              onChange={(_, option) => setSelectedOption(option)}
              options={selectableOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Failure reason"
                  placeholder="Select..."
                  sx={{ mb: 0 }}
                />
              )}
              sx={{ flexGrow: 1 }}
              value={selectedOption}
            />
            <Button
              disabled={isSaving || !selectedOption}
              onClick={addSelectedReason}
              variant="contained"
            >
              Add
            </Button>
          </Stack>

          {drafts.map((draft) => {
            const missingAmount = needsBudgetAmount(draft.reason, draft.comment, passedReasons);
            return (
              <Stack
                key={draft.reason}
                sx={{
                  backgroundColor: (theme) => theme.palette.common.grey50,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  p: 2,
                }}
              >
                <Chip
                  label={<Box pr={0.5}>{getFailureReasonLabel(draft.reason, screeningCriteriaMap)}</Box>}
                  onDelete={isSaving ? undefined : () => {
                    setDrafts((current) => current.filter((item) => item.reason !== draft.reason));
                  }}
                  sx={{
                    alignSelf: 'flex-start',
                    backgroundColor: (theme) => theme.palette.common.error10,
                  }}
                />
                <TextField
                  disabled={isSaving}
                  error={missingAmount}
                  fullWidth
                  helperText={missingAmount
                    ? "This trip's budget already meets the destination minimum. Enter the minimum for this trip (e.g. $490)."
                    : undefined}
                  label="Explanation"
                  required={missingAmount}
                  minRows={2}
                  multiline
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: (theme) => theme.palette.common.white,
                    },
                  }}
                  onChange={(event) => {
                    const { value } = event.target;
                    setDrafts((current) => current.map((item) => (
                      item.reason === draft.reason
                        ? { ...item, comment: value }
                        : item
                    )));
                  }}
                  value={draft.comment ?? ''}
                />
              </Stack>
            );
          })}
        </Stack>
      </FormDialogContent>
      <FormDialogActions sx={{ mt: 0 }}>
        <FormCancelButton onClick={handleClose} />
        <LoadingButton
          onClick={() => onSubmit(drafts)}
          variant="contained"
          {...getSubmitButtonProps(isSaving, drafts.length > 0 && !hasMissingAmount)}
        >
          Submit
        </LoadingButton>
      </FormDialogActions>
    </FormDialog>
  );
};

export default AddFailureReasonDialog;
